import axios from 'axios'

export interface Address {
  address_id: string
  user_id: string
  type: 'home' | 'work' | 'other'
  street: string
  city: string
  postal_code: string
  country: string
  latitude: number | null
  longitude: number | null
  is_default: boolean
  created_at: string
  user_first_name: string | null
  user_last_name: string | null
}

export interface CreateAddressDto {
  user_id: string
  type?: 'home' | 'work' | 'other'
  street: string
  city: string
  postal_code: string
  country: string
  latitude?: number
  longitude?: number
  is_default?: boolean
}

export interface UpdateAddressDto {
  type?: 'home' | 'work' | 'other'
  street?: string
  city?: string
  postal_code?: string
  country?: string
  latitude?: number
  longitude?: number
  is_default?: boolean
}

export interface PaginatedResponse<T> {
  data: T[]
  total: number
  success: boolean
}

export interface AddressFilters {
  user_id?: string
  type?: string
}

// Use proxy configuration from vite.config.ts
const API_BASE_URL = '/api'

const addressApi = axios.create({
  baseURL: `${API_BASE_URL}/addresses`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add request interceptor for authentication
addressApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Add response interceptor for error handling
addressApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('access_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export const addressService = {
  // Get all addresses with optional filters
  async getAll(filters: AddressFilters = {}): Promise<PaginatedResponse<Address>> {
    const params = new URLSearchParams()
    
    if (filters.user_id) params.append('user_id', filters.user_id)
    if (filters.type) params.append('type', filters.type)

    const response = await addressApi.get('', { params })
    return response.data
  },

  // Get address by ID
  async getById(id: string): Promise<{ success: boolean; data: Address }> {
    const response = await addressApi.get(`/${id}`)
    return response.data
  },

  // Get addresses by user ID
  async getByUserId(userId: string): Promise<PaginatedResponse<Address>> {
    const response = await addressApi.get(`/user/${userId}`)
    return response.data
  },

  // Create new address
  async create(addressData: CreateAddressDto): Promise<{ success: boolean; data: Address; message: string }> {
    const response = await addressApi.post('', addressData)
    return response.data
  },

  // Update address
  async update(id: string, addressData: UpdateAddressDto): Promise<{ success: boolean; data: Address; message: string }> {
    const response = await addressApi.put(`/${id}`, addressData)
    return response.data
  },

  // Delete address
  async delete(id: string): Promise<{ success: boolean; message: string }> {
    const response = await addressApi.delete(`/${id}`)
    return response.data
  },

  // Set address as default
  async setAsDefault(id: string): Promise<{ success: boolean; data: Address; message: string }> {
    const response = await addressApi.patch(`/${id}/default`)
    return response.data
  },

  // Get address statistics
  async getStatistics(): Promise<{
    total: number
    byType: Record<string, number>
    defaultCount: number
    byCountry: Record<string, number>
  }> {
    const allAddresses = await this.getAll()
    const addresses = allAddresses.data
    
    const byType: Record<string, number> = {}
    const byCountry: Record<string, number> = {}
    
    addresses.forEach(address => {
      byType[address.type] = (byType[address.type] || 0) + 1
      byCountry[address.country] = (byCountry[address.country] || 0) + 1
    })
    
    const stats = {
      total: addresses.length,
      byType,
      defaultCount: addresses.filter(a => a.is_default).length,
      byCountry
    }
    
    return stats
  }
}

export default addressService
